<template>
  <CareDataContent
    :fetch-results
    :status-filter-option="openTasksStatusOptions"
  >
    <template #custom-content>
      <template v-if="shouldDisplayCustomerCareDashboardLinks">
        <RevDivider />

        <div class="my-32 flex space-x-12">
          <RevButton
            size="small"
            target="_blank"
            :to="returnDetailsLinkValue"
            variant="secondary"
            @click="trackClickOnReturnDetailsButton"
          >
            {{ i18n(translations.returnDetailsButton) }}
          </RevButton>
          <RevLink @click="openReturnDetailsModal">
            {{ i18n(translations.returnDetailsOpenModalButton) }}
          </RevLink>
        </div>
      </template>

      <RevInfoBlock
        class="body-2-bold mb-32"
        :icon="IconInfo"
        :title="i18n(translations.infoBlockContent)"
        variant="info"
      />

      <RevDrawer
        backdrop-z-index="z-30"
        :name="modalName"
        :title="i18n(translations.returnDetailsModalHeader)"
      >
        <template #body>
          <div class="text-action-default-hi space-y-24">
            <div class="font-weight-heading-1 heading-1">
              {{ i18n(translations.returnDetailsModalTitle) }}
            </div>
            <div class="font-weight-body-1 text-body-1">
              {{ i18n(translations.returnDetailsModalDescription) }}
            </div>
            <RevTextList variant="ordered">
              <RevTextListItem>
                <FormattedMessage
                  :definition="
                    translations.returnDetailsModalGuideStepOneFirstParagraph
                  "
                >
                  <template #returndetailslink>
                    <RevLink
                      target="_blank"
                      :to="returnDetailsLinkValue"
                      underlined
                      @click="trackClickOnReturnDetailsLink"
                    >
                      {{
                        i18n(
                          translations.returnDetailsModalGuideStepOneLinkLabel,
                        )
                      }}
                    </RevLink>
                  </template>
                </FormattedMessage>
                <div class="mt-24">
                  {{
                    i18n(
                      translations.returnDetailsModalGuideStepOneSecondParagraph,
                    )
                  }}
                </div>
              </RevTextListItem>
              <RevTextListItem>{{
                i18n(translations.returnDetailsModalGuideStepTwo)
              }}</RevTextListItem>
              <RevTextListItem>{{
                i18n(translations.returnDetailsModalGuideStepThree)
              }}</RevTextListItem>
            </RevTextList>
          </div>
        </template>
      </RevDrawer>
    </template>

    <template #title="{ totalCount }">
      <h2 class="text-heading-2">
        {{
          i18n(translations.taskTotalCount, {
            count: totalCount,
          })
        }}
      </h2>
    </template>

    <template #table="{ data }">
      <OpenTasksTable :requests="data || []" />
    </template>

    <template #empty-state="{ hasFiltersOn, totalCount }">
      <NoRequestFoundBlock v-if="hasFiltersOn">
        <RevLink :to="allRequestsLink">
          <span class="body-1-bold">
            {{ i18n(translations.noRequestFoundLinkLabel) }}
          </span>
        </RevLink>
      </NoRequestFoundBlock>
      <NoOpenTaskBlock v-if="totalCount === 0 && !hasFiltersOn" />
    </template>
  </CareDataContent>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevTextList } from '@ds/components/TextList'
import { RevTextListItem } from '@ds/components/TextListItem'
import { IconInfo } from '@ds/icons/IconInfo'

import type { RequestStatusType } from '~/api/seller-after-sales/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { MODAL_NAMES } from '~/scopes/customer-care/modalNames'

import CareDataContent from '../CareDataContent/CareDataContent.vue'
import { EMPTY_FILTERS_VALUES } from '../Filters/Filters.constants'
import filtersTranslations from '../Filters/Filters.translations'
import NoRequestFoundBlock from '../NoRequestFoundBlock/NoRequestFoundBlock.vue'

import translations from './OpenTasks.translations'
import NoOpenTaskBlock from './components/NoOpenTaskBlock/NoOpenTaskBlock.vue'
import OpenTasksTable from './components/OpenTasksTable/OpenTasksTable.vue'

const i18n = useI18n()
const tracking = useTracking()
const $httpFetch = useHttpFetch()

const emit = defineEmits(['update-task-count'])

const returnDetailsLinkValue = {
  name: ROUTE_NAMES.INSIGHTS.INDEX,
  query: {
    tableau: 'customer-care',
    subview: 'details',
    view: 'Returns',
  },
}

const modalName = MODAL_NAMES.RETURN_DETAILS

function openReturnDetailsModal() {
  openModal(modalName)
  tracking.trackClick({
    name: 'return_details_open_drawer_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
  })
}

function trackClickOnReturnDetailsButton() {
  tracking.trackClick({
    name: 'return_details_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
  })
}

function trackClickOnReturnDetailsLink() {
  tracking.trackClick({
    name: 'return_details_drawer_link_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
  })
}

const allRequestsLink = {
  name: ROUTE_NAMES.CUSTOMER_CARE.ALL_REQUESTS,
}

emit('update-task-count')

const openTasksStatusOptions = computed(() => {
  return [
    {
      label: i18n(filtersTranslations.statusFilterLateOption),
      value: 'LATE' as RequestStatusType,
    },
  ]
})

const {
  FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_COUNTRY,
  FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_SELLER_ID,
} = useRuntimeConfig().public
const { market } = useMarketplace()
const { publicId } = useSellerInfoStore()

const shouldDisplayCustomerCareDashboardLinks = computed(() => {
  const hiddenCountries = (
    FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_COUNTRY || ''
  )
    .toString()
    .split(',')

  const hiddenSellerIds = (
    FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_SELLER_ID || ''
  )
    .toString()
    .split(',')

  const isHiddenForCurrentSeller = publicId
    ? hiddenSellerIds.includes(publicId.toString())
    : false

  return (
    !hiddenCountries.includes(market.countryCode) && !isHiddenForCurrentSeller
  )
})

function fetchResults(query: Partial<typeof EMPTY_FILTERS_VALUES>) {
  return $httpFetch('/seller-after-sales/api/v1/requests', {
    query: { sort: 'DEADLINE', status: 'TODO', ...query },
  })
}
</script>
